import type {
  EngageConnectOutcome,
  EngageDomainCheck,
  EngageMailbox,
} from '@/store/features/engage.feature';

export type ConnectProvider = 'google' | 'microsoft';

export interface ConnectProviderOption {
  key: ConnectProvider;
  /** The engage provider key, for the provider mark. */
  mark: 'google_workspace' | 'microsoft_365';
  /** The provider's own name: "Connect Google", "Waiting for Google". */
  name: string;
  /** The choice's label. */
  label: string;
  /** What a domain needs in its SPF record to send through this provider. */
  spfInclude: string;
  /** The provider's page on turning DKIM on. */
  dkimGuide: string;
}

export const CONNECT_PROVIDERS: ConnectProviderOption[] = [
  {
    key: 'google',
    mark: 'google_workspace',
    name: 'Google',
    label: 'Google Workspace or Gmail',
    spfInclude: '_spf.google.com',
    dkimGuide: 'https://support.google.com/a/answer/174124',
  },
  {
    key: 'microsoft',
    mark: 'microsoft_365',
    name: 'Microsoft',
    label: 'Microsoft 365 or Outlook',
    spfInclude: 'spf.protection.outlook.com',
    dkimGuide:
      'https://learn.microsoft.com/defender-office-365/email-authentication-dkim-configure',
  },
];

export function connectProvider(key: ConnectProvider): ConnectProviderOption {
  return CONNECT_PROVIDERS.find((option) => option.key === key)!;
}

/** The provider option for an engage provider key (`google_workspace`...). */
export function connectProviderFor(provider: string): ConnectProviderOption {
  return connectProvider(provider === 'microsoft_365' ? 'microsoft' : 'google');
}

/**
 * The page the hosted sign-in returns to tells the dialog that opened it over
 * this channel (same origin, so it works when the provider's pages have cut
 * the window's `opener`), then closes itself.
 */
export const CONNECT_CHANNEL = 'cleanlist-mailbox-connect';
export type ConnectReturn = {
  type: 'connect-return';
  ok: boolean;
  attemptId?: string;
};

export function isConnectReturn(message: unknown): message is ConnectReturn {
  return (
    !!message &&
    typeof message === 'object' &&
    (message as { type?: unknown }).type === 'connect-return' &&
    typeof (message as { ok?: unknown }).ok === 'boolean' &&
    ((message as { attemptId?: unknown }).attemptId === undefined ||
      typeof (message as { attemptId?: unknown }).attemptId === 'string')
  );
}

/** The sign-in window: centred over the window the dialog is in. */
export function popupFeatures(): string {
  const width = 520;
  const height = 700;
  const left = Math.max(
    0,
    Math.round(window.screenX + (window.outerWidth - width) / 2)
  );
  const top = Math.max(
    0,
    Math.round(window.screenY + (window.outerHeight - height) / 2)
  );
  return `popup=yes,width=${width},height=${height},left=${left},top=${top}`;
}

// ── The flow ────────────────────────────────────────────────────────────────

export type ConnectPhase =
  | { kind: 'choose' }
  | { kind: 'starting'; provider: ConnectProvider }
  /** The sign-in window is open; `returned` once it has reported back. */
  | { kind: 'waiting'; provider: ConnectProvider; returned: boolean }
  /** The window closed and nothing was connected. */
  | { kind: 'closed'; provider: ConnectProvider }
  /** The window reported back, but the address is not showing yet. */
  | { kind: 'slow'; provider: ConnectProvider }
  | { kind: 'checks'; mailboxId: string }
  | {
      kind: 'already';
      mailboxId: string;
      email: string;
      ownerId: string;
    }
  | { kind: 'refused'; message: string }
  /** The link could not be made, or the provider ended the sign-in. */
  | { kind: 'failed'; provider: ConnectProvider; message: string };

export type ConnectEvent =
  | { type: 'pick'; provider: ConnectProvider }
  | { type: 'opened' }
  | { type: 'startFailed'; message: string; refused?: boolean }
  | { type: 'windowReturned'; ok: boolean }
  | { type: 'windowClosed' }
  | { type: 'gaveUp' }
  | { type: 'recheck' }
  | { type: 'outcome'; outcome: EngageConnectOutcome }
  | { type: 'mailbox'; mailboxId: string }
  | { type: 'restart' };

export const CHOOSE: ConnectPhase = { kind: 'choose' };

const PROVIDER_FAILED = 'The sign-in did not finish.';
const REFUSED = 'This mailbox could not be added.';

/** Every move of the connect dialog, so each state and its exits are testable. */
export function connectReducer(
  phase: ConnectPhase,
  event: ConnectEvent
): ConnectPhase {
  switch (event.type) {
    case 'pick':
      // Try again after a failure starts a new sign-in.
      return phase.kind === 'choose' ||
        phase.kind === 'failed' ||
        phase.kind === 'closed' ||
        phase.kind === 'slow'
        ? { kind: 'starting', provider: event.provider }
        : phase;
    case 'opened':
      return phase.kind === 'starting'
        ? { kind: 'waiting', provider: phase.provider, returned: false }
        : phase;
    case 'startFailed':
      return phase.kind === 'starting'
        ? event.refused
          ? { kind: 'refused', message: event.message }
          : { kind: 'failed', provider: phase.provider, message: event.message }
        : phase;
    case 'windowReturned':
      if (!isWaiting(phase) || !('provider' in phase)) return phase;
      return event.ok
        ? { kind: 'waiting', provider: phase.provider, returned: true }
        : {
            kind: 'failed',
            provider: phase.provider,
            message: PROVIDER_FAILED,
          };
    case 'windowClosed':
      // A window the page closed after reporting back is the normal end of
      // the sign-in, not a cancel.
      return phase.kind === 'waiting' && !phase.returned
        ? { kind: 'closed', provider: phase.provider }
        : phase;
    case 'gaveUp':
      return phase.kind === 'waiting'
        ? { kind: 'slow', provider: phase.provider }
        : phase;
    case 'recheck':
      return phase.kind === 'slow'
        ? { kind: 'waiting', provider: phase.provider, returned: true }
        : phase;
    case 'outcome': {
      // The answer ends any wait, whatever the window did.
      if (!isWaiting(phase)) return phase;
      const { outcome } = event;
      if (outcome.status === 'connected')
        return { kind: 'checks', mailboxId: outcome.mailbox_id };
      if (outcome.status === 'already_connected')
        return {
          kind: 'already',
          mailboxId: outcome.mailbox_id,
          email: outcome.email_address,
          ownerId: outcome.owner_user_id,
        };
      if (outcome.status === 'refused')
        return { kind: 'refused', message: outcome.message || REFUSED };
      return phase;
    }
    case 'mailbox':
      return isWaiting(phase)
        ? { kind: 'checks', mailboxId: event.mailboxId }
        : phase;
    case 'restart':
      return CHOOSE;
  }
}

/** Whether the dialog is waiting on the provider or the API. */
export function isWaiting(phase: ConnectPhase): boolean {
  return (
    phase.kind === 'waiting' || phase.kind === 'closed' || phase.kind === 'slow'
  );
}

/**
 * The mailbox a sign-in added: an email address that was not in the list when
 * the sign-in began, or one that needed a reconnect and no longer does. The
 * list is the fallback when the outcome cannot be read.
 */
export function findConnected(
  before: ReadonlyMap<string, boolean>,
  now: EngageMailbox[],
  userId: string | null | undefined
): EngageMailbox | null {
  return (
    now.find((mailbox) => {
      if (
        !userId ||
        mailbox.owner_user_id !== userId ||
        mailbox.provider === 'linkedin'
      )
        return false;
      const was = before.get(mailbox.id);
      return was === undefined ? mailbox.connected : !was && mailbox.connected;
    }) ?? null
  );
}

export function snapshotMailboxes(
  mailboxes: EngageMailbox[]
): Map<string, boolean> {
  return new Map(mailboxes.map((mailbox) => [mailbox.id, mailbox.connected]));
}

// ── The checks ──────────────────────────────────────────────────────────────

export type DomainRowKey = 'spf' | 'dkim' | 'dmarc';
export type DomainRowState = 'found' | 'missing' | 'incomplete' | 'unknown';

export interface DnsRecord {
  type: 'TXT';
  host: string;
  value: string;
  /** Added to the record the domain already has, not a new one. */
  appendTo?: boolean;
}

export interface DomainRow {
  key: DomainRowKey;
  label: string;
  state: DomainRowState;
  /** The chip: Found, Missing, Incomplete, Not found, Not read. */
  chip: string;
  /** The record to add, when one fixes it. */
  record?: DnsRecord;
  /** DKIM keys are made in the provider's admin console, not copied. */
  guide?: string;
}

/**
 * SPF, DKIM and DMARC as rows, each with the record that fixes it when one
 * can: SPF and DMARC have a fixed record; a DKIM key is the provider's to
 * make. Null for a personal mail domain, which has no records of its own, and
 * for a domain not checked yet.
 */
export function domainRows(
  check: EngageDomainCheck | null,
  provider: ConnectProviderOption
): DomainRow[] | null {
  if (!check || check.status === 'provider') return null;
  const { spf, dkim, dmarc } = check;
  return [
    {
      key: 'spf',
      label: 'SPF',
      state:
        spf === 'pass'
          ? 'found'
          : spf === 'missing'
            ? 'missing'
            : spf === 'no_provider'
              ? 'incomplete'
              : 'unknown',
      chip:
        spf === 'pass'
          ? 'Found'
          : spf === 'missing'
            ? 'Missing'
            : spf === 'no_provider'
              ? 'Incomplete'
              : 'Not read',
      record:
        spf === 'missing'
          ? {
              type: 'TXT',
              host: '@',
              value: `v=spf1 include:${provider.spfInclude} ~all`,
            }
          : spf === 'no_provider'
            ? {
                type: 'TXT',
                host: '@',
                value: `include:${provider.spfInclude}`,
                appendTo: true,
              }
            : undefined,
    },
    {
      key: 'dkim',
      label: 'DKIM',
      state: dkim === 'pass' ? 'found' : 'unknown',
      chip: dkim === 'pass' ? 'Found' : 'Not found',
      guide: dkim === 'pass' ? undefined : provider.dkimGuide,
    },
    {
      key: 'dmarc',
      label: 'DMARC',
      state:
        dmarc === 'pass'
          ? 'found'
          : dmarc === 'missing'
            ? 'missing'
            : 'unknown',
      chip:
        dmarc === 'pass'
          ? 'Found'
          : dmarc === 'missing'
            ? 'Missing'
            : 'Not read',
      record:
        dmarc === 'missing'
          ? { type: 'TXT', host: '_dmarc', value: 'v=DMARC1; p=none;' }
          : undefined,
    },
  ];
}
